import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Circle, Square, X, MicOff } from "lucide-react";
import { toast } from "sonner";

const MIME_TYPES = ["video/mp4", "video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"];

export const canRecordInApp = () =>
  typeof window !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof window.MediaRecorder !== "undefined";

const pickMime = () => MIME_TYPES.find((t) => window.MediaRecorder.isTypeSupported?.(t)) || "";

// Full-screen camera that records video only: the microphone is never requested.
export function SilentRecorder({ open, maxSeconds, onDone, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const chunks = useRef([]);
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);

  const stopStream = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false })
      .then((s) => {
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) videoRef.current.srcObject = s;
      })
      .catch(() => { toast.error("Camera access was blocked. Allow the camera, or upload a video instead."); onClose(); });
    return () => { cancelled = true; recRef.current?.state === "recording" && recRef.current.stop(); stopStream(); setRecording(false); setSecs(0); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!recording) return undefined;
    const t = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [recording]);

  useEffect(() => {
    if (recording && secs >= maxSeconds) stop();
  }, [secs, recording]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = () => {
    if (!streamRef.current) return;
    const mime = pickMime();
    const rec = new window.MediaRecorder(streamRef.current, mime ? { mimeType: mime, videoBitsPerSecond: 2_500_000 } : undefined);
    chunks.current = [];
    rec.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
    rec.onstop = () => {
      const type = rec.mimeType || mime || "video/webm";
      const ext = type.includes("mp4") ? "mp4" : "webm";
      const blob = new Blob(chunks.current, { type });
      stopStream();
      if (blob.size) onDone(new File([blob], `room-video.${ext}`, { type: type.split(";")[0] }));
      else onClose();
    };
    rec.start(1000);
    recRef.current = rec;
    setSecs(0);
    setRecording(true);
  };

  const stop = () => { setRecording(false); recRef.current?.state === "recording" && recRef.current.stop(); };

  if (!open) return null;
  const mm = String(Math.floor(secs / 60)).padStart(1, "0");
  const ss = String(secs % 60).padStart(2, "0");
  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="silent-recorder">
      <video ref={videoRef} autoPlay playsInline muted className="flex-1 w-full object-cover" data-testid="silent-recorder-preview" />
      <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-white">
        <span className="flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-xs" data-testid="silent-recorder-no-audio"><MicOff className="h-3.5 w-3.5" /> No sound is recorded</span>
        {!recording && <button type="button" onClick={onClose} aria-label="Close camera" className="h-9 w-9 rounded-full bg-black/60 flex items-center justify-center" data-testid="silent-recorder-close"><X className="h-5 w-5" /></button>}
      </div>
      <div className="absolute bottom-0 inset-x-0 pb-8 pt-6 bg-gradient-to-t from-black/80 to-transparent flex flex-col items-center gap-3">
        <span className={`font-mono text-sm ${recording ? "text-red-400" : "text-white/80"}`} data-testid="silent-recorder-timer">{mm}:{ss} / {Math.floor(maxSeconds / 60)}:00</span>
        {recording ? (
          <Button type="button" onClick={stop} className="h-16 w-16 rounded-full bg-white hover:bg-white/90 p-0" aria-label="Stop recording" data-testid="silent-recorder-stop"><Square className="h-6 w-6 fill-red-600 text-red-600" /></Button>
        ) : (
          <Button type="button" onClick={start} className="h-16 w-16 rounded-full bg-red-600 hover:bg-red-700 p-0 ring-4 ring-white" aria-label="Start recording" data-testid="silent-recorder-start"><Circle className="h-7 w-7 fill-white text-white" /></Button>
        )}
        <p className="text-xs text-white/70">{recording ? "Walk slowly around the room" : "Tap to start filming"}</p>
      </div>
    </div>
  );
}
